import DataStructure, { sanitize } from "./DataStructure.js";
import { deepFreeze, timestampToDate } from "../util/misc.js";
import { every, get, isEmpty as _isEmpty } from "lodash";
import { diff } from "deep-object-diff";

export const CALL_LOG_FIELDS = {
  userID: null,
  contactID: null,
  contactName: "", // free-text name when the contact isn't a known contact
  date: null,
  notes: "",
  followUpDate: null,
  followUpAction: "",

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(CALL_LOG_FIELDS);

export default class CallLog extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, CALL_LOG_FIELDS);

    // Convert to Date
    data.date = timestampToDate(data.date);
    data.followUpDate = timestampToDate(data.followUpDate);
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      view: CallLog.canBeViewedBy,
      create: CallLog.canBeCreatedBy,
      edit: CallLog.canBeEditedBy,
      delete: CallLog.canBeDeletedBy,
    });
  }

  // Permissions

  static canBeViewedBy(user, doc) {
    if (user.isAdmin) return true;
    if (doc && doc.data.userID === user.id) return true;
    return false;
  }

  static canBeCreatedBy(user) {
    return user.isEmployee;
  }

  static canBeEditedBy(user, doc) {
    if (user.isAdmin) return true;
    if (doc && doc.data.userID === user.id) return true;
    return false;
  }

  static canBeDeletedBy(user, doc) {
    if (user.isAdmin) return true;
    if (doc && doc.data.userID === user.id) return true;
    return false;
  }

  // Database

  get path() {
    return CallLog.getPath(this.id);
  }

  static getPath(id) {
    if (id) {
      return `callLogs/${id}`;
    }
    return "callLogs";
  }

  static getStateID(path) {
    const match = path.match(/^callLogs\/([^/]+)$/);
    if (match) {
      return match[1];
    }
    return null;
  }

  get stateID() {
    return this.id;
  }

  toFirestore() {
    return {
      ...this.data,
    };
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new CallLog(snapshot.id, data);
  }

  // Editing

  get rules() {
    return {
      contactID: [
        (a) => Boolean(a || (this.data.contactName || "").trim()) || "required",
      ],
      date: [(a) => Boolean(a) || "required"],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(this.data, key)) === true)
    );
  }

  get isEmpty() {
    return Boolean(!this.id && _isEmpty(diff(this.data, CALL_LOG_FIELDS)));
  }

  isEqual(callLog) {
    return Boolean(
      this.id === callLog.id && _isEmpty(diff(this.data, callLog.data))
    );
  }
}
